import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./PracticeAssessment.module.css";
import quiz from "../../json/Quiz.json";
import { submitQuizAssessment } from "../../lib/store";

import TopBar from "../EdofSectionNew2/TopBar/TopBar";
import Navbar from "../LandingPage/Navbar/Navbar";
import Footer2 from "../Footer2/Footer2";

import AssessmentBackground from "./AssessmentBackground/AssessmentBackground";
import EventHero from "./EventHero/EventHero";
import AssessmentIntro from "./AssessmentIntro/AssessmentIntro";
import Assessment2Quiz from "./Assessment2Quiz/Assessment2Quiz";
import Assessment2Form from "./Assessment2Form/Assessment2Form";
import Assessment2Fitting from "./Assessment2Fitting/Assessment2Fitting";
import Assessment2ThankYou from "./Assessment2ThankYou/Assessment2ThankYou";
import StepProgress from "./StepProgress/StepProgress";
import QuestionCard from "./QuestionCard/QuestionCard";
import ChoiceList from "./ChoiceList/ChoiceList";
import ReadinessDropdowns from "./ReadinessDropdowns/ReadinessDropdowns";
import UserInfoForm, { validateUserInfo } from "./UserInfoForm/UserInfoForm";
import ThankYouPanel from "./ThankYouPanel/ThankYouPanel";

const QUESTIONS_PER_PAGE = 2;

// Flow phases: Q1–Q6 over three quiz pages, then a combined details + fitting
// step (left = form, right = fitting panel, with Q7 dropdowns when interested).
const PHASE = {
  INTRO: "intro",
  QUIZ: "quiz",
  FORM: "form",
  THANKYOU: "thankyou",
};

const emptyUser = {
  firstName: "",
  lastName: "",
  practiceName: "",
  role: "",
  zipCode: "",
  workEmail: "",
  npi: "",
};

// localStorage keys — selections are kept here so nothing is lost on refresh
// and the full payload is ready to POST later (no API wired yet).
const getStorageKeys = (variant) => {
  const suffix = variant === "assessment2" ? "_assessment2" : "";
  return {
    draft: `bvc_practice_assessment${suffix}`,
    submission: `bvc_practice_assessment_submission${suffix}`,
  };
};

// Restore any in-progress answers/details from a previous visit.
const loadSaved = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Map a 0–100 total to its letter grade + label (matches Quiz.json grades).
const pickGrade = (total) => {
  if (total >= 75) return { grade: "A", label: "Hot" };
  if (total >= 55) return { grade: "B", label: "Warm: prioritize" };
  if (total >= 35) return { grade: "C", label: "Develop: nurture, or work if they ask for a kit" };
  return { grade: "D", label: "Low: light-touch or nurture" };
};

const PAIN_KEEP_FACTORS = [0.3, 0.6, 0.85, 1.0];

const assessment2Intro = {
  ...quiz.intro,
  eyebrow: "Practice Assessment 2",
  heading: "Find the next growth opportunity in your presbyopia practice.",
  highlight: "growth",
  description:
    "Use the same quick assessment flow to identify patient opportunity, fitting readiness, and whether Deseyne may support your next step.",
  ctaLabel: "Start assessment",
};

const PracticeAssessment = ({ variant = "default", startAtQuiz = false }) => {
  const navigate = useNavigate();
  const isAssessment2 = variant === "assessment2";
  // Route for the standalone assessment-2 quiz flow (opens on its own page).
  const ASSESSMENT2_QUIZ_PATH = "/academy-2026-anaheim/partner-with-us";
  const ASSESSMENT2_INTRO_PATH = "/academy-2026-anaheim";
  const storageKeys = useMemo(() => getStorageKeys(variant), [variant]);
  const savedState = useMemo(() => loadSaved(storageKeys.draft), [storageKeys.draft]);
  // Flatten every section's questions into a single ordered list (Q1…Q7).
  const allQuestions = useMemo(
    () => quiz.sections.flatMap((section) => section.questions),
    []
  );
  const pagedQuestions = allQuestions.slice(0, 6); // Q1–Q6, two per page
  const finalQuestion = allQuestions[6]; // Q7 (grouped readiness)
  const totalQuizPages = Math.ceil(pagedQuestions.length / QUESTIONS_PER_PAGE);

  const [phase, setPhase] = useState(startAtQuiz ? PHASE.QUIZ : PHASE.INTRO);
  const [quizPage, setQuizPage] = useState(0);
  // Assessment 2 always starts clean — no answer is pre-selected on load.
  const [answers, setAnswers] = useState(() => savedState?.answers || {});
  const [fittingAnswer, setFittingAnswer] = useState(() =>
    savedState?.fittingAnswer || null
  );
  const [userInfo, setUserInfo] = useState(() =>
    savedState?.userInfo || emptyUser
  );
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // Mobile-only: the combined step is split into "form" then "fitting".
  const [mobileStep, setMobileStep] = useState("form");
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 860px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 860px)");
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Refs used to auto-advance between questions on a quiz page.
  const questionRefs = useRef({});
  const actionsRef = useRef(null);
  const fittingPanelRef = useRef(null);
  const dropdownsRef = useRef(null);
  const prevValidRef = useRef(false);
  const prevInterestedRef = useRef(false);
  // Once submitted, stop persisting the draft so a completed assessment is
  // never restored on the next visit.
  const submittedRef = useRef(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [phase, quizPage]);

  const interested = fittingAnswer?.value === "yes";

  // --- scoring -----------------------------------------------------------
  // Score = Fit (0–60) + Intent (0–40). Uses the selected option's `score`.
  const scoreOf = (id) => Number(answers[id]?.score) || 0;

  const computeScore = () => {
    // FIT (0–60): Size (Q1 + Q2, capped 30) + Pain (max(Q3, Q4) × keep, capped 30).
    // Pain takes the *stronger* of retention/friction — not their sum — then scales
    // it by practice volume, because a problem only counts when patients are behind it.
    const size = Math.min(scoreOf(1) + scoreOf(2), 30);
    const q1 = allQuestions.find((q) => q.id === 1);
    const q1Idx = q1 ? q1.options.findIndex((o) => o.label === answers[1]?.label) : -1;
    const keep = q1Idx >= 0 ? PAIN_KEEP_FACTORS[q1Idx] : 0;
    const pain = Math.min(Math.max(scoreOf(3), scoreOf(4)) * keep, 30);
    const fit = Math.round(size + pain);

    // INTENT (0–40): Q5 + Q6 + Q7 readiness (max 10, only when interested).
    const q7sel = answers[finalQuestion.id] || {};
    const q7raw = finalQuestion.groups.reduce(
      (sum, _g, gi) => sum + (Number(q7sel[gi]?.score) || 0),
      0
    );
    const q7 = interested ? Math.min(q7raw, finalQuestion.max || 10) : 0;
    const intent = scoreOf(5) + scoreOf(6) + q7;

    const total = fit + intent;
    const grade = pickGrade(total);
    return {
      fit,
      intent,
      total,
      grade: grade.grade,
      gradeLabel: grade.label,
      breakdown: { size, pain, q5: scoreOf(5), q6: scoreOf(6), q7 },
    };
  };

  const score = computeScore();

  // Persist selections + live score to localStorage on every change, so a
  // refresh keeps progress and the full data is ready to send when the API
  // is wired later. (No network call here — local storage only.)
  useEffect(() => {
    // After a successful submission, don't recreate the draft.
    if (submittedRef.current) return;
    try {
      localStorage.setItem(
        storageKeys.draft,
        JSON.stringify({
          answers,
          fittingAnswer,
          userInfo,
          score,
          updatedAt: new Date().toISOString(),
        })
      );
    } catch {
      /* storage unavailable (e.g. private mode) — ignore */
    }
  }, [answers, fittingAnswer, userInfo, score, storageKeys.draft]);

  // --- current quiz page + completeness ---------------------------------
  const pageQuestions = pagedQuestions.slice(
    quizPage * QUESTIONS_PER_PAGE,
    quizPage * QUESTIONS_PER_PAGE + QUESTIONS_PER_PAGE
  );

  const revealIfNeeded = (el, block) => {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (!(rect.top >= 0 && rect.bottom <= vh)) el.scrollIntoView({ behavior: "smooth", block });
  };

  const autoAdvance = (questionId) => {
    const idx = pageQuestions.findIndex((q) => q.id === questionId);
    if (idx === -1) return;
    const next = pageQuestions[idx + 1];
    const target = next ? questionRefs.current[next.id] : actionsRef.current;
    window.requestAnimationFrame(() => revealIfNeeded(target, next ? "center" : "nearest"));
  };

  const handleChange = (questionId, option, groupIndex) => {
    setAnswers((prev) => {
      if (groupIndex === undefined) return { ...prev, [questionId]: option };
      return {
        ...prev,
        [questionId]: { ...(prev[questionId] || {}), [groupIndex]: option },
      };
    });
    if (groupIndex === undefined && phase === PHASE.QUIZ) autoAdvance(questionId);
    // Q7: after the last sub-question, slide the "See my results" button up.
    if (
      groupIndex !== undefined &&
      finalQuestion &&
      questionId === finalQuestion.id &&
      groupIndex === finalQuestion.groups.length - 1
    ) {
      window.requestAnimationFrame(() => revealIfNeeded(actionsRef.current, "nearest"));
    }
  };

  const pageComplete = pageQuestions.every((q) => Boolean(answers[q.id]));
  const userInfoValid = Object.keys(validateUserInfo(userInfo)).length === 0;
  const finalComplete =
    finalQuestion && finalQuestion.groups.every((_, gi) => answers[finalQuestion.id]?.[gi]);
  // Ready to submit the combined details + fitting step.
  const canSubmit = userInfoValid && !!fittingAnswer && (!interested || finalComplete);

  // When the form first becomes valid, slide the fitting panel into view
  // (mainly for mobile, where it sits below the form). Only scrolls if it
  useEffect(() => {
    if (phase === PHASE.FORM && userInfoValid && !prevValidRef.current) {
      window.requestAnimationFrame(() => revealIfNeeded(fittingPanelRef.current, "center"));
    }
    prevValidRef.current = userInfoValid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfoValid, phase]);

  // When "Yes, if we qualify" is selected, slide the Q7 dropdowns into view
  // (mobile — they render below the options after the panel expands).
  useEffect(() => {
    if (phase === PHASE.FORM && interested && !prevInterestedRef.current) {
      window.requestAnimationFrame(() => revealIfNeeded(dropdownsRef.current, "center"));
    }
    prevInterestedRef.current = interested;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interested, phase]);

  // --- navigation --------------------------------------------------------
  const goToQuiz = () => {
    // Assessment 2 opens the quiz on its own page/route.
    if (isAssessment2 && !startAtQuiz) {
      navigate(ASSESSMENT2_QUIZ_PATH);
      return;
    }
    setQuizPage(0);
    setPhase(PHASE.QUIZ);
  };

  const handleQuizNext = () => {
    if (quizPage < totalQuizPages - 1) setQuizPage((p) => p + 1);
    else {
      setMobileStep("form");
      setPhase(PHASE.FORM);
    }
  };

  const handleQuizBack = () => {
    if (quizPage > 0) setQuizPage((p) => p - 1);
    else if (startAtQuiz) navigate(ASSESSMENT2_INTRO_PATH);
    else setPhase(PHASE.INTRO);
  };

  const handleSubmit = async () => {
    setFormSubmitAttempted(true);
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    const payload = {
      details: {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        practiceName: userInfo.practiceName,
        role: userInfo.role,
        state: userInfo.state || "",
        city: userInfo.city || "",
        zipCode: userInfo.zipCode || "",
        workEmail: userInfo.workEmail,
        npi: userInfo.npi || "",
      },
      answers: allQuestions
        .filter((q) => q.id !== finalQuestion.id || interested)
        .map((q) => ({ id: q.id, title: q.title, question: q.content, answer: answers[q.id] || null })),
      fittingSet: fittingAnswer,
      score, // { fit, intent, total, grade, gradeLabel, breakdown }
      grade: score.grade,
      gradeLabel: score.gradeLabel,
      submittedAt: new Date().toISOString(),
    };
    try {
      // Keep a local backup, then submit to the backend.
      localStorage.setItem(storageKeys.submission, JSON.stringify(payload));
      await submitQuizAssessment(payload);
      // Submission succeeded — stop persisting and clear all local progress so
      // the next visit starts fresh (no old answers/details restored).
      submittedRef.current = true;
      localStorage.removeItem(storageKeys.draft);
      localStorage.removeItem(storageKeys.submission);
      setPhase(PHASE.THANKYOU);
    } catch (err) {
      console.error("Practice Assessment submission failed", err);
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- segmented progress (3 quiz pages + the combined details step) -----
  const totalSteps = totalQuizPages + 1;
  const stepInfo = () => {
    switch (phase) {
      case PHASE.QUIZ:
        return { current: quizPage + 1, label: "Your practice" };
      case PHASE.FORM:
        return { current: totalSteps, label: "Your details" };
      default:
        return null;
    }
  };
  const step = stepInfo();

  // --- thank-you summary (kicker + selected answer) ----------------------
  const buildSummary = () => {
    // Q7 (readiness) is intentionally excluded from the thank-you summary.
    const rows = pagedQuestions.map((q) => ({
      kicker: q.title,
      answer: answers[q.id]?.label,
    }));
    rows.push({ kicker: quiz.fittingSet.title, answer: fittingAnswer?.label });
    return rows;
  };

  // --- render ------------------------------------------------------------
  const renderBody = () => {
    switch (phase) {
      case PHASE.INTRO:
        return (
          <AssessmentIntro
            intro={isAssessment2 ? assessment2Intro : quiz.intro}
            onBegin={goToQuiz}
            variant={variant}
          />
        );

      case PHASE.QUIZ:
        if (isAssessment2) {
          return (
            <Assessment2Quiz
              questions={pageQuestions}
              startNumber={quizPage * QUESTIONS_PER_PAGE + 1}
              stepIndex={quizPage}
              answers={answers}
              onChange={handleChange}
              onBack={handleQuizBack}
              onNext={handleQuizNext}
              onExit={() => navigate("/")}
              pageComplete={pageComplete}
            />
          );
        }
        return (
          <>
            <div className={styles.questionsGrid}>
              {pageQuestions.map((q, i) => (
                <div key={q.id} ref={(el) => (questionRefs.current[q.id] = el)}>
                  <QuestionCard
                    question={q}
                    number={quizPage * QUESTIONS_PER_PAGE + i + 1}
                    value={answers[q.id]}
                    onChange={handleChange}
                  />
                </div>
              ))}
            </div>
            <div className={styles.actions} ref={actionsRef}>
              <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={handleQuizBack}>
                Back
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleQuizNext}
                disabled={!pageComplete}
              >
                Next
              </button>
            </div>
          </>
        );

      case PHASE.FORM: {
        // Assessment 2: two sequential full screens (on every size) — the
        // details form (Step 4 of 4) then the fitting-set request (final step).
        if (isAssessment2) {
          if (mobileStep === "fitting") {
            return (
              <Assessment2Fitting
                fittingAnswer={fittingAnswer}
                onSelectFitting={setFittingAnswer}
                finalQuestion={finalQuestion}
                answers={answers}
                onChange={handleChange}
                interested={interested}
                onExit={() => navigate("/")}
                onBack={() => setMobileStep("form")}
                onSubmit={handleSubmit}
                submitDisabled={!canSubmit || submitting}
                submitting={submitting}
                submitError={submitError}
              />
            );
          }
          return (
            <Assessment2Form
              userInfo={userInfo}
              setUserInfo={setUserInfo}
              submitAttempted={formSubmitAttempted}
              onExit={() => navigate("/")}
              onBack={() => {
                setQuizPage(totalQuizPages - 1);
                setPhase(PHASE.QUIZ);
              }}
              onNext={() => {
                setFormSubmitAttempted(true);
                if (userInfoValid) {
                  setMobileStep("fitting");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              nextDisabled={false}
            />
          );
        }

        // On mobile the step is sequential: fill the form, then the fitting
        // step appears. On desktop both columns show side-by-side.
        const showForm = !isMobile || mobileStep === "form";
        const showPanel = !isMobile || mobileStep === "fitting";
        const mobileFormStep = isMobile && mobileStep === "form";

        return (
          <>
            <div
              className={`${styles.snapshotWrap} ${
                interested && !isMobile ? styles.snapshotWrapExpanded : ""
              }`}
            >
              {/* Left — details form (collapses when "Yes" is chosen on desktop) */}
              {showForm && (
                <div className={styles.snapshotForm} aria-hidden={interested && !isMobile}>
                  <span className={styles.eyebrow}>{quiz.form.eyebrow}</span>
                  <h2 className={`${styles.heading} ${styles.headingSub}`}>{quiz.form.heading}</h2>
                  <p className={styles.lede}>{quiz.form.description}</p>
                  <UserInfoForm
                    values={userInfo}
                    onChange={setUserInfo}
                    submitAttempted={formSubmitAttempted}
                    roleOptions={quiz.form.roleOptions}
                    consent={quiz.form.consent}
                  />
                </div>
              )}

              {/* Right — fitting panel. Blurred until valid on desktop; on
                  mobile it only appears once the form is done. */}
              {showPanel && (
                <div
                  ref={fittingPanelRef}
                  className={`${styles.snapshotSide} ${
                    !isMobile && !userInfoValid ? styles.snapshotSideBlur : ""
                  }`}
                >
                  <div className={styles.sideHead}>
                    <span className={styles.eyebrow} style={{ margin: 0 }}>
                      {quiz.fittingSet.eyebrow}
                    </span>
                    <span className={styles.requiredTag}>{quiz.fittingSet.requiredLabel}</span>
                  </div>
                  <h3 className={styles.fittingQ}>{quiz.fittingSet.heading}</h3>
                  <ChoiceList
                    name="fittingSet"
                    options={quiz.fittingSet.options}
                    value={fittingAnswer}
                    onSelect={setFittingAnswer}
                    ariaLabel={quiz.fittingSet.heading}
                  />
                  {interested && (
                    <div ref={dropdownsRef}>
                      <ReadinessDropdowns
                        question={finalQuestion}
                        value={answers[finalQuestion.id]}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {submitError && (
              <p className={styles.errorText} style={{ marginBottom: "0.75rem" }}>
                {submitError}
              </p>
            )}
            <div className={styles.actions} ref={actionsRef}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={() => {
                  if (isMobile && mobileStep === "fitting") {
                    setMobileStep("form");
                  } else if (interested && !isMobile) {
                    setFittingAnswer(null);
                  } else {
                    setQuizPage(totalQuizPages - 1);
                    setPhase(PHASE.QUIZ);
                  }
                }}
              >
                Back
              </button>

              {mobileFormStep ? (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={() => {
                    setMobileStep("fitting");
                    // Wait for the panel to mount, then scroll it into view.
                    requestAnimationFrame(() =>
                      requestAnimationFrame(() =>
                        fittingPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                      )
                    );
                  }}
                  disabled={!userInfoValid}
                >
                  Next step
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                >
                  {submitting ? "Submitting…" : "See my results"}
                </button>
              )}
            </div>
          </>
        );
      }

      case PHASE.THANKYOU:
        if (isAssessment2) {
          return (
            <Assessment2ThankYou
              thankYou={quiz.thankYou}
              userInfo={userInfo}
              summary={buildSummary()}
              onHome={() => navigate("/")}
            />
          );
        }
        return (
          <ThankYouPanel
            thankYou={quiz.thankYou}
            userInfo={userInfo}
            summary={buildSummary()}
            onHome={() => navigate("/")}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <TopBar />
      <Navbar compact={startAtQuiz} />
      <div className={styles.page}>
      
        <AssessmentBackground />
        {!startAtQuiz && <EventHero />}

        <div
          className={`${styles.shell} ${
            phase === PHASE.QUIZ || phase === PHASE.FORM || phase === PHASE.THANKYOU
              ? styles.shellWide
              : ""
          }`}
        >
          <div
            className={`${styles.card} ${
              isAssessment2 && phase === PHASE.INTRO ? styles.cardIntroTwo : ""
            } ${
              isAssessment2 &&
              (phase === PHASE.QUIZ || phase === PHASE.FORM || phase === PHASE.THANKYOU)
                ? styles.cardAssessmentTwo
                : ""
            }`}
          >
            <span className={styles.cardDecor} aria-hidden="true">
              <span className={styles.decorGlow} />
              <span className={styles.decorRingOuter} />
              <span className={styles.decorRingInner} />
            </span>
            <div className={styles.cardContent}>
              {step && !(isAssessment2 && (phase === PHASE.QUIZ || phase === PHASE.FORM)) && (
                <StepProgress total={totalSteps} current={step.current} label={step.label} />
              )}
              {renderBody()}
            </div>
          </div>
        </div>
      </div>
      <Footer2 />
    </div>
  );
};

export default PracticeAssessment;
